import { useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { evaluateLock } from '../lib/lock.js';
import { useDoorHydrated } from '../lib/useDoorHydrated.js';
import { useMeasurementsReadiness } from '../lib/useMeasurements.js';
import { LockHandleStep } from '../steps/LockHandleStep.js';
import { useDoorStore, type GuardNotice } from '../stores/doorStore.js';
import { StepProgress } from './StepProgress.js';

export function RequireMeasurements({
  requireLock = false,
  allowUnverifiedCatalogue = false,
  currentIndex = 2,
  children,
}: {
  requireLock?: boolean;
  allowUnverifiedCatalogue?: boolean;
  currentIndex?: number;
  children?: ReactNode;
}) {
  const hydrated = useDoorHydrated();
  const { draft, evaluation, catalogue, products, retry } = useMeasurementsReadiness();
  const guardNotice = useDoorStore((state) => state.guardNotice);
  const setGuardNotice = useDoorStore((state) => state.setGuardNotice);
  const navigate = useNavigate();
  const locallyReady =
    evaluation.readyExceptCatalogue &&
    evaluation.size !== undefined &&
    evaluation.size.width > 0 &&
    evaluation.size.height > 0;
  const catalogueUnverified =
    evaluation.availability.status === 'loading' ||
    evaluation.availability.status === 'error' ||
    evaluation.availability.status === 'offline';
  const canShowUnverified = allowUnverifiedCatalogue && catalogueUnverified && locallyReady;

  let destination: '/planner/type' | '/planner/measure' | '/planner/lock' | undefined;
  let notice: GuardNotice | undefined;
  if (hydrated) {
    if (!draft.doorType) {
      destination = '/planner/type';
      notice = 'door-type';
    } else if (!locallyReady) {
      destination = '/planner/measure';
      notice = 'measurements';
    } else if (
      !canShowUnverified &&
      evaluation.availability.status !== 'loading' &&
      !evaluation.canContinue
    ) {
      destination = '/planner/measure';
      notice = 'catalogue';
    } else if (
      (evaluation.canContinue || canShowUnverified) &&
      requireLock &&
      !evaluateLock(draft.lock, draft.doorType, {
        size: evaluation.size,
        canContinue: evaluation.canContinue || canShowUnverified,
      }).canContinue
    ) {
      destination = '/planner/lock';
      notice = 'lock';
    }
  }

  useEffect(() => {
    if (destination && notice) {
      setGuardNotice(notice);
      void navigate({ to: destination, replace: true });
    }
  }, [destination, navigate, notice, setGuardNotice]);

  useEffect(() => {
    if (hydrated && evaluation.canContinue && guardNotice && guardNotice !== 'lock') {
      setGuardNotice(null);
    }
  }, [evaluation.canContinue, guardNotice, hydrated, setGuardNotice]);

  if (!hydrated || destination || (!evaluation.canContinue && !canShowUnverified)) {
    return (
      <>
        <StepProgress currentIndex={currentIndex} />
        <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10">
          <p role="status">Checking your measurements and catalogue availability...</p>
        </main>
      </>
    );
  }
  return (
    children ?? (
      <LockHandleStep
        draft={draft}
        measurements={evaluation}
        catalogue={catalogue}
        products={products}
        retry={retry}
      />
    )
  );
}
